import { Button } from "@heroui/react";
import { type AdminEpisodeDetail, type AdminGoldenAsset, type AdminSeriesDetail, ProductionState } from "@storyline/shared";
import { Link } from "react-router";
import Pill from "@/src/common/components/pill";
import SectionCard from "@/src/common/components/section-card";
import type { ReferenceRequirement } from "@/src/stories/utils/required-references";
import { requiredReferences } from "@/src/stories/utils/required-references";

type ReferencesGateProps = {
	episode: AdminEpisodeDetail;
	series: AdminSeriesDetail;
	assets: AdminGoldenAsset[];
	onTransition: (to: ProductionState) => void;
};

const RequirementRow: React.FC<{ requirement: ReferenceRequirement; kindLabel: string }> = ({ requirement, kindLabel }) => (
	<li className="flex items-center justify-between rounded-lg border border-border-soft bg-glass p-3 text-sm">
		<span className="text-foreground">
			{requirement.name} <span className="text-muted text-xs">· {kindLabel}</span>
		</span>
		{requirement.approved ? <Pill tone="success">Approuvé</Pill> : <Pill tone="danger">Manquant</Pill>}
	</li>
);

const ReferencesGate: React.FC<ReferencesGateProps> = ({ episode, series, assets, onTransition }) => {
	const references = requiredReferences(episode, series.characters, series.locations, assets);

	return (
		<SectionCard
			title="Gate des références"
			description="Chaque personnage à l'écran doit avoir un visage approuvé, chaque lieu un plan maître approuvé."
			actions={
				<Link to={`/series/${series.id}`} className="text-accent-lav text-sm hover:underline">
					Ouvrir le Bible Studio
				</Link>
			}
		>
			<div className="flex flex-col gap-4">
				<div className="grid gap-4 md:grid-cols-2">
					<div className="flex flex-col gap-2">
						<span className="font-semibold text-muted text-xs uppercase tracking-wide">Personnages ({references.characters.length})</span>
						{references.characters.length === 0 ? (
							<p className="text-muted text-sm">Aucun personnage requis.</p>
						) : (
							<ul className="flex flex-col gap-2">
								{references.characters.map((requirement) => (
									<RequirementRow key={requirement.id} requirement={requirement} kindLabel="Visage officiel" />
								))}
							</ul>
						)}
					</div>
					<div className="flex flex-col gap-2">
						<span className="font-semibold text-muted text-xs uppercase tracking-wide">Lieux ({references.locations.length})</span>
						{references.locations.length === 0 ? (
							<p className="text-muted text-sm">Aucun lieu requis.</p>
						) : (
							<ul className="flex flex-col gap-2">
								{references.locations.map((requirement) => (
									<RequirementRow key={requirement.id} requirement={requirement} kindLabel="Plan maître" />
								))}
							</ul>
						)}
					</div>
				</div>
				<div className="flex items-center justify-between gap-2">
					<Button variant="ghost" className="text-muted" onPress={() => onTransition(ProductionState.STORY_REVIEW)}>
						← Retour à l'histoire
					</Button>
					<Button isDisabled={!references.allApproved} onPress={() => onTransition(ProductionState.KEYFRAMES_GENERATING)}>
						Lancer la génération des keyframes
					</Button>
				</div>
			</div>
		</SectionCard>
	);
};

export default ReferencesGate;
